import type { ToastPlacement, ToastRecord } from './Toast.common';

/** A stack pins to a screen edge; `inline` is by definition not a stack. */
export type ToastStackPlacement = Exclude<ToastPlacement, 'inline'>;

export type ToastStackProps = {
  /** Oldest first. Usually `useToast().toastStackProps.toasts`. */
  toasts: readonly ToastRecord[];
  /** Called once a toast has finished its exit and can leave the list. */
  onDismiss: (id: string) => void;
  /** Called as a toast begins its exit, ahead of `onDismiss`. `useToast` flags the record. */
  onExitStart?: (id: string) => void;
  /** The edge the stack pins to; the newest toast sits nearest it. Defaults to `bottom`. */
  placement?: ToastStackPlacement;
  /** A further arrival evicts the oldest rather than queueing. Defaults to MAX_VISIBLE_TOASTS. */
  maxVisible?: number;
  testID?: string;
};

export const TOAST_STACK_TEST_ID = 'toast-stack';

export const DEFAULT_STACK_PLACEMENT: ToastStackPlacement = 'bottom';

export const getNeighborIds = (
  orderedIds: readonly string[],
  removedId: string
): { newer?: string; older?: string } => {
  const index = orderedIds.indexOf(removedId);
  if (index === -1) return {};

  return { newer: orderedIds[index + 1], older: orderedIds[index - 1] };
};

const isFocusable = (element: Element | null | undefined): element is HTMLElement =>
  element instanceof HTMLElement && element.isConnected;

/**
 * Where focus goes when the toast holding it is removed: the newer neighbor's ✕, else the older
 * one's, else where focus was before the first toast, else the container. Never the body.
 */
export const getNextFocusTarget = ({
  newerNeighbor,
  olderNeighbor,
  previouslyFocused,
  container,
}: {
  newerNeighbor?: HTMLElement | null;
  olderNeighbor?: HTMLElement | null;
  previouslyFocused?: Element | null;
  container: HTMLElement | null;
}): HTMLElement | null => {
  if (isFocusable(newerNeighbor)) return newerNeighbor;
  if (isFocusable(olderNeighbor)) return olderNeighbor;
  if (isFocusable(previouslyFocused) && previouslyFocused !== document.body) {
    return previouslyFocused;
  }
  return container;
};
